/**
 * Canonical LOGICAL script-section model.
 *
 * The canonical Main Script ("Florida Phone Sales Script") is stored as a
 * long flat `script_sections[]` array (~164 raw blocks). Those raw blocks are
 * NOT sales sections: they include Section Description, Important Info,
 * Actual Script Pathway, objection/interruption Cards, Section Notes,
 * End Of Section and objection-loop bodies.
 *
 * The single source of truth for "how many sections does this script have",
 * "what shows up in the agent quick nav" and "what number/title the agent
 * sees" is this module. It reuses the existing structural parser
 * (`groupSuperSections`) and applies the canon:
 *
 *   - each top-level main-flow group (1, 2, 4, 5, ... 10B, 11) is ONE section
 *   - the B1 objection area is a CONTAINER; it never counts on its own
 *   - each B1 objection loop IS its own logical navigable section/screen
 *   - cards / setup / notes / exit blocks are CONTENT inside a section
 *
 * NUMBERING CONTRACT: the agent-facing numbering is the normalized 1..15
 * academy sequence published in the training lesson "The Main Script Is Your
 * Recipe for Success" — never the legacy raw H1 prefixes (which skip 3 and
 * include 10B / B1). Stored script wording is never mutated; this is a
 * presentation-layer normalization only.
 */
import {
  groupSuperSections,
  type RawSection,
  type SuperChild,
  type SuperSection,
} from "@/lib/scriptSuperSections";

/**
 * The canonical normalized 15-step sequence (training contract).
 * Order: the 11 main-flow sections, then the 4 closing loops.
 */
export const CANONICAL_15_TITLES: readonly string[] = [
  "Pre-Presentation Objections",
  "Opening and File Authentication",
  "Qualifying",
  "Selling The Problem",
  "Presenting The Program",
  "Savings Calculator and Energy Savings Report",
  "Results and Lower Obligation Close",
  "Funding Authorization Signing",
  "Inspection Time Selection",
  "Secure Form Pre-Frame and Walkthrough",
  "TPV Prep and Verification Alignment",
  "Deflection Loop",
  "Pain Threshold A",
  "Pain Threshold B",
  "Lower Action Threshold",
] as const;

const CANONICAL_MAIN_COUNT = 11;
const CANONICAL_LOOP_COUNT = 4;

export interface LogicalScriptSection {
  /** Stable key for react + nav anchors. */
  key: string;
  /** Normalized 1-based step number shown to the agent. */
  number: number;
  /** Normalized title (canonical training title when the script matches). */
  title: string;
  /** Label shown in the quick nav / pager: "1. Pre-Presentation Objections". */
  label: string;
  /** Index into the grouped super-section array (the rendered container). */
  superIndex: number;
  /** "section" = normal main-flow group, "loop" = a B1 objection loop. */
  kind: "section" | "loop";
  /** For loops: the flat index of the loop child (drives open/scroll). */
  loopFlatIndex?: number;
  /** Anchor id to scroll to. */
  anchor: string;
  /** Legacy source prefix ("1", "10B", "B1") — internal only, never shown. */
  rawPrefix: string;
  /** Legacy source title — internal only, never shown. */
  rawTitle: string;
  /** True when this loop is the first loop of its container. */
  isFirstOfContainer?: boolean;
  /** True when this loop is the last loop of its container. */
  isLastOfContainer?: boolean;
}

export function shortLabel(prefix: string, shortTitle: string): string {
  if (!prefix) return shortTitle;
  if (/^B\d+$/i.test(prefix)) return `${prefix}: ${shortTitle}`;
  return `Section ${prefix}: ${shortTitle}`;
}

/**
 * The loop children of a container (B1) super-section. Prefer explicitly
 * classified "loop" rows; fall back to script-pathway rows inside a loops
 * container so an unlabeled variant still yields navigable entries.
 */
export function loopChildren(group: SuperSection): SuperChild[] {
  const explicit = group.children.filter((c) => c.group === "loop");
  if (explicit.length > 0) return explicit;
  if (!group.isLoops) return [];
  return group.children.filter((c) => c.group === "live" || c.group === "other");
}

/** Strip a legacy "Loop 3: " / "1. " style prefix off a raw title. */
function stripRawPrefix(raw: string): string {
  return raw
    .trim()
    .replace(/^loop\s*\d+\s*[:.\-]\s*/i, "")
    .replace(/^(\d+[A-Z]?|B\d+)\.\s*/i, "")
    .trim();
}

/**
 * Derive the logical navigation model from grouped super-sections.
 * Cards are never logical sections. Numbering is normalized 1..N.
 */
export function deriveLogicalSections(groups: SuperSection[]): LogicalScriptSection[] {
  interface Draft {
    key: string;
    superIndex: number;
    kind: "section" | "loop";
    loopFlatIndex?: number;
    anchor: string;
    rawPrefix: string;
    rawTitle: string;
    fallbackTitle: string;
    isFirstOfContainer?: boolean;
    isLastOfContainer?: boolean;
  }

  const drafts: Draft[] = [];
  groups.forEach((g, superIndex) => {
    const loops = loopChildren(g);
    if (g.isLoops && loops.length > 0) {
      // Container: contributes only its loops. B1 itself is never a section.
      loops.forEach((loop, i) => {
        drafts.push({
          key: `${g.id}-loop-${loop.flatIndex}`,
          superIndex,
          kind: "loop",
          loopFlatIndex: loop.flatIndex,
          anchor: `${g.id}-loop-${loop.flatIndex}`,
          rawPrefix: g.prefix,
          rawTitle: loop.rawTitle.trim(),
          fallbackTitle: stripRawPrefix(loop.rawTitle) || `${g.prefix} Loop ${i + 1}`,
          isFirstOfContainer: i === 0,
          isLastOfContainer: i === loops.length - 1,
        });
      });
      return;
    }
    drafts.push({
      key: g.id,
      superIndex,
      kind: "section",
      anchor: g.id,
      rawPrefix: g.prefix,
      rawTitle: g.title,
      fallbackTitle: g.shortTitle || g.title,
    });
  });

  const mainCount = drafts.filter((d) => d.kind === "section").length;
  const loopCount = drafts.filter((d) => d.kind === "loop").length;
  const useCanonical =
    drafts.length === CANONICAL_15_TITLES.length &&
    mainCount === CANONICAL_MAIN_COUNT &&
    loopCount === CANONICAL_LOOP_COUNT;

  return drafts.map((d, i) => {
    const title = useCanonical ? CANONICAL_15_TITLES[i] : d.fallbackTitle;
    return {
      key: d.key,
      number: i + 1,
      title,
      label: `${i + 1}. ${title}`,
      superIndex: d.superIndex,
      kind: d.kind,
      loopFlatIndex: d.loopFlatIndex,
      anchor: d.anchor,
      rawPrefix: d.rawPrefix,
      rawTitle: d.rawTitle,
      isFirstOfContainer: d.isFirstOfContainer,
      isLastOfContainer: d.isLastOfContainer,
    };
  });
}

/**
 * Map legacy source prefixes ("1", "10B", "B1") to their normalized
 * agent-facing label, so render-time transition text such as
 * "[Next Section] Section 10B: …" can be rewritten without touching storage.
 */
export function buildLegacyPrefixMap(entries: LogicalScriptSection[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const e of entries) {
    if (e.kind !== "section" || !e.rawPrefix) continue;
    map[e.rawPrefix.toUpperCase()] = `Step ${e.number}: ${e.title}`;
  }
  return map;
}

const SECTION_REF_RE = /\bSection\s+(\d+[A-Z]?|B\d+)\s*:?\s*/gi;

/**
 * Presentation-layer normalization of legacy section references inside a
 * rendered HTML fragment. Never mutates stored script wording.
 */
export function normalizeSectionReferences(
  html: string,
  prefixMap: Record<string, string>,
): string {
  if (!html) return html;
  // Legacy "Section 12: Verification Call Handoff…" transition first.
  const pre = normalizeLegacyTransitionText(html);
  return pre.replace(SECTION_REF_RE, (match, prefix: string) => {
    const hit = prefixMap[String(prefix).toUpperCase()];
    if (!hit) return match;
    const trailingSpace = /\s$/.test(match) ? " " : "";
    return `${hit}${trailingSpace}`;
  });
}

/**
 * Logical section count for any script. Grouped/canonical scripts resolve
 * through the logical model; legacy/plain scripts fall back safely to the
 * raw block count so nothing breaks.
 */
export function getLogicalSectionCount(sections: RawSection[] | undefined | null): number {
  if (!sections || sections.length === 0) return 0;
  const groups = groupSuperSections(sections);
  if (!groups) return sections.length;
  return deriveLogicalSections(groups).length;
}

/**
 * STATIC normalized display map, keyed by the legacy raw source key stored on
 * training lesson anchors ("1", "2", "4", "5", "6", "7", "8", "9", "10",
 * "10B", "11", "B1"). This is the render-only bridge between stored source
 * keys and the published 1..15 training contract. Storage is never mutated.
 */
export const SOURCE_KEY_TO_STEP_NUMBER: Readonly<Record<string, number>> = {
  "1": 1,
  "2": 2,
  "4": 3,
  "5": 4,
  "6": 5,
  "7": 6,
  "8": 7,
  "9": 8,
  "10": 9,
  "10B": 10,
  "11": 11,
};

/** Generic closing-loops container: never shown as "B1" to a learner. */
export const CLOSING_LOOPS_LABEL = "Steps 12-15: Closing Loops";

/** Canonical step title by normalized 1-based step number. */
export function canonicalStepTitle(step: number): string | null {
  return CANONICAL_15_TITLES[step - 1] ?? null;
}

/**
 * Normalized learner-facing heading for a raw source key.
 * Returns null when the key is not part of the canonical Main Script.
 */
export function normalizedStepLabelForSourceKey(
  sourceKey: string | null | undefined,
): string | null {
  if (!sourceKey) return null;
  const key = String(sourceKey).trim().toUpperCase();
  if (/^B\d+$/.test(key)) return CLOSING_LOOPS_LABEL;
  const step = SOURCE_KEY_TO_STEP_NUMBER[key];
  if (!step) return null;
  const title = canonicalStepTitle(step);
  return title ? `Step ${step}: ${title}` : null;
}

/**
 * Legacy render-time transition artifact: the source text preceding the B1
 * closing-loop area announces "Section 12: Verification Call Handoff and
 * Final Wrap". Under the canonical contract Step 12 is the Deflection Loop,
 * so the visible transition is normalized. Source rows are untouched.
 */
const LEGACY_SECTION_12_RE =
  /\bSection\s*12\s*:?\s*Verification Call Handoff(?:\s*and\s*Final Wrap)?/gi;

export function normalizeLegacyTransitionText(html: string): string {
  if (!html) return html;
  return html.replace(LEGACY_SECTION_12_RE, "Step 12: Deflection Loop");
}

/**
 * Replace ONLY the leading source <h1> of a rendered section fragment with
 * the normalized step heading. Every approved script line below it is
 * preserved byte-for-byte.
 */
export function replaceSourceHeading(html: string, normalizedHeading: string | null): string {
  if (!html || !normalizedHeading) return html;
  return html.replace(
    /<h1\b[^>]*>[\s\S]*?<\/h1>/i,
    `<h1>${normalizedHeading}</h1>`,
  );
}
